import React from "react";
import Moment from "react-moment";
import { Package, MapPin, Phone, Clock, CheckCircle2, XCircle } from "lucide-react";
import "./ViewResource.css";

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  borderBottom: "1px solid #EEF2F6",
  paddingBottom: "8px",
  marginBottom: "14px",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  color: "#2E6DA4",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
};

const valueStyle = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#0F2D42",
  lineHeight: 1.5,
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "18px",
};

const badgeStyle = (active, activeText, inactiveText) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "10.5px",
  fontWeight: 700,
  padding: "3px 10px",
  borderRadius: "999px",
  background: active ? "#E8F5E9" : "#F1F5F9",
  color: active ? "#2E7D32" : "#64748B",
  border: `1px solid ${active ? "#A5D6A7" : "#E2E8F0"}`,
});

const ResourceInfo = ({ resourceData }) => {
  if (!resourceData) {
    return (
      <div className="resv-page">
        <div className="resv-card resv-empty">No resource data available.</div>
      </div>
    );
  }

  const totalQuantity = resourceData.total_quantity || 0;
  const damagedQuantity = resourceData.damaged_quantity || 0;
  const deployQuantity = resourceData.deploy_quantity || 0;
  const availableQuantity = totalQuantity - damagedQuantity - deployQuantity;

  const isActive = resourceData.status === true;
  const isDeployed = resourceData.is_deployed === true;
  const isMandatory = resourceData.is_mandatory === true;
  const hasRequirement =
    resourceData.is_mandatory === true || resourceData.is_mandatory === false;

  return (
    <div className="resv-page">
      <div className="resv-card resv-card-body" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* ── Basic Information ── */}
        <div>
          <div style={sectionTitleStyle}>Basic Information</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <Package size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Resource Name
              </label>
              <div style={valueStyle}>{resourceData.resource_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Resource Category</label>
              <div style={valueStyle}>{resourceData.rsrc_cat_id || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Agency Name</label>
              <div style={valueStyle}>{resourceData.agency_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Resource Requirement</label>
              <div style={badgeStyle(isMandatory)}>
                {isMandatory ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                {hasRequirement ? (isMandatory ? "Mandatory" : "Optional") : "NA"}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stock ── */}
        <div>
          <div style={sectionTitleStyle}>Stock</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>Total Quantity (Incl. OOS/Damaged)</label>
              <div style={valueStyle}>{resourceData.total_quantity ?? "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>OOS/Damaged Quantity</label>
              <div style={valueStyle}>{resourceData.damaged_quantity ?? "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Deployed Quantity</label>
              <div style={valueStyle}>{resourceData.deploy_quantity ?? "0"}</div>
            </div>
            <div>
              <label style={labelStyle}>Available Quantity</label>
              <div style={valueStyle}>{availableQuantity >= 0 ? availableQuantity : 0}</div>
            </div>
            <div>
              <label style={labelStyle}>Deployed</label>
              <div style={badgeStyle(isDeployed)}>
                {isDeployed ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                {resourceData.is_deployed === true || resourceData.is_deployed === false ? (isDeployed ? "Yes" : "No") : "NA"}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <div style={badgeStyle(isActive)}>
                {isActive ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                {resourceData.status === true || resourceData.status === false ? (isActive ? "Active" : "Inactive") : "NA"}
              </div>
            </div>
          </div>
        </div>

        {/* ── Location ── */}
        <div>
          <div style={sectionTitleStyle}>Location</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <MapPin size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                District
              </label>
              <div style={valueStyle}>{resourceData.dist_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <MapPin size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Block
              </label>
              <div style={valueStyle}>{resourceData.region_name || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>Facility</label>
              <div style={valueStyle}>{resourceData.facility || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Contact ── */}
        <div>
          <div style={sectionTitleStyle}>Contact</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>Resource Activity</label>
              <div style={valueStyle}>{resourceData.rsrc_activity_id || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Phone size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Contact Info
              </label>
              <div style={valueStyle}>{resourceData.contact_info || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Timeline ── */}
        <div>
          <div style={sectionTitleStyle}>Timeline</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Created On
              </label>
              <div style={valueStyle}>
                {resourceData.created_on ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">{resourceData.created_on}</Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Created By</label>
              <div style={valueStyle}>{resourceData.created_by || "NA"}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={{ marginRight: "4px", verticalAlign: "-1px" }} />
                Updated On
              </label>
              <div style={valueStyle}>
                {resourceData.updated_on ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">{resourceData.updated_on}</Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Updated By</label>
              <div style={valueStyle}>{resourceData.updated_by || "NA"}</div>
            </div>
          </div>
        </div>

        {/* ── Description ── */}
        <div>
          <div style={sectionTitleStyle}>Description</div>
          <div
            style={{
              ...valueStyle,
              fontWeight: 500,
              background: "#EBF4FA",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              padding: "12px 14px",
            }}
          >
            {resourceData.description || "NA"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceInfo;
